import { useCallback, useEffect, useRef, useState } from 'react'
import {
    listCatalogItems, updateCatalogItem, listAuthors,
    type CatalogItem, type AuthorRow, type FeeType,
} from '../api'
import { useTabs, useDataVersion, useDirtyGuard, stableJson, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import InfoTip from '../components/InfoTip'
import { brandName, moneySign } from '../lookups'
import { checkBeforeSave } from '../forms/checkForm'
import { LOOKUP_FETCH_LIMIT } from '../../shared/constants'
import { plural, fill, PAGES_CATALOG_ITEM_FORM_TEXTS as T } from '../../shared/texts'

// The commercial side of ONE article. Title, type and the editorial release belong to
// vitalpilze and are shown here as context only — writing them here would create a second
// truth that the next report overwrites anyway.

// Only a COURSE can belong to an author (operator, 2026-08-10) — recordings and webinars are
// sold too, but nobody is settled for them.
const canHaveAuthor = (type?: string) => !type || type === 'vp_course'

const money = (n?: number) => n == null ? '—' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const FEE_LEVEL: Record<string, string> = T.feeLevel

// The form holds TEXT ("19"), the record holds a NUMBER (19). Baseline and snapshot must be
// built by this ONE function, or the mask reads as changed from the first render and stays
// that way — the same trap that kept "Übernehmen" enabled in the role editor.
const toForm = (r: CatalogItem) => ({
    salesPrice: r.salesPrice == null ? '' : String(r.salesPrice),
    feeType: (r.feeType ?? '') as string,
    feeValue: r.feeValue == null ? '' : String(r.feeValue),
    authorId: r.authorId ?? '',
})
type Form = ReturnType<typeof toForm>
const EMPTY: Form = { salesPrice: '', feeType: '', feeValue: '', authorId: '' }

// "19,00" from the keyboard, 19 for the API. '' means "not set" — the server maps it to
// undefined, which is not the same as 0.
const num = (s: string) => s.trim() === '' ? null : Number(s.replace(',', '.'))

export default function CatalogItemForm({ tabId, id, itemId }: { tabId?: string, id?: string, itemId?: number }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { publishChange } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    const readOnly = !can('invoices', 'write') // role lacks invoices:write → view only
    const version = useDataVersion('catalog')
    // The author choices follow AuthorForm/AuthorsList (publishChange('authors')) on their own:
    // only the options reload, the record and any unsaved input stay untouched.
    const authorsVersion = useDataVersion('authors')

    const [rec, setRec] = useState<CatalogItem | null>(null)
    const [authors, setAuthors] = useState<AuthorRow[]>([])
    const [form, setForm] = useState<Form>(EMPTY)
    const [baseline, setBaseline] = useState<Form | null>(null) // null until loaded
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const dirty = baseline !== null && stableJson(form) !== stableJson(baseline)
    // Read by the reload effect, which must not overwrite what is being typed.
    const dirtyRef = useRef(dirty)
    dirtyRef.current = dirty

    // There is no GET /authors/items/:id — and this task must not touch the server. The list
    // endpoint matches on the article id as well, so asking for it and picking our record out
    // is the way to load a single article without inventing a route.
    // ⚠️ Its limit: should more than 200 records ever match this search and ours fall outside
    // the page, the mask would report "not in the catalogue any more" although the article is
    // fine. Out of reach at today's size — but that is the first thing to check if that
    // message ever appears for an article that plainly exists.
    const load = useCallback(async () => {
        if (!id) return
        try {
            const [items, auth] = await Promise.all([
                listCatalogItems({ q: itemId == null ? '' : String(itemId), pageSize: LOOKUP_FETCH_LIMIT }),
                // The dropdown needs every author, and the list endpoint pages at 50 by default.
                listAuthors({ pageSize: LOOKUP_FETCH_LIMIT }),
            ])
            setAuthors(auth.rows)
            const found = items.rows.find((r) => r._id === id)
            if (!found) { setError(T.notInCatalog); return }
            setRec(found)
            setError('')
            const next = toForm(found)
            setBaseline(next)
            setForm(next)
        } catch (e: any) {
            setError(e?.response?.data?.error ?? e?.message ?? T.loadFailed)
        }
    }, [id, itemId])

    useEffect(() => { void load() }, [load])

    useEffect(() => {
        if (authorsVersion === 0) return // the first load above already brought the authors
        listAuthors({ pageSize: LOOKUP_FETCH_LIMIT }).then((r) => setAuthors(r.rows)).catch(() => {})
    }, [authorsVersion])

    // Someone else touched the catalog (release toggled, another tab saved). Refresh — but
    // never on top of unsaved input: silently replacing what the operator is typing is the
    // very loss this mask exists to prevent.
    useEffect(() => {
        if (version === 0 || dirtyRef.current) return
        void load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version])

    const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }))

    async function save(): Promise<SaveResult> {
        if (!id) return { ok: false, error: T.noItemLoaded }
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy(true); setError('')
        try {
            await updateCatalogItem(id, {
                salesPrice: num(form.salesPrice) as any,
                feeType: (form.feeType || null) as FeeType,
                feeValue: num(form.feeValue) as any,
                authorId: (form.authorId || null) as any,
            })
            // PUT answers {ok:true}, not the record — the resolved fee and the version only
            // come back with a fresh read, and load() sets the baseline from it.
            publishChange('catalog')
            await load()
            toast(fill(T.saved, { title: rec?.title || `#${rec?.itemId}` }))
            return { ok: true }
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(msg)
            return { ok: false, error: msg }
        } finally { setBusy(false) }
    }

    function submit(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !readOnly, save)

    if (!rec) {
        return <div><h1>{T.item}</h1><p className="muted">{error || T.loading}</p></div>
    }

    const withAuthor = canHaveAuthor(rec.type)
    const suggestionDiffers = rec.priceSuggestion != null && rec.priceSuggestion !== rec.salesPrice

    return (
        <form ref={formRef} noValidate onSubmit={submit} className="form-page">
            <div className="form-top">
                <div className="form-header">
                    <h1>
                        {readOnly ? T.titleView : T.titleEdit}{' '}
                        <InfoTip>
                            {T.info.part1}<b>{T.info.bold1}</b>{T.info.part2}<b>{T.info.bold2}</b>{T.info.part3}
                        </InfoTip>
                    </h1>
                    {!readOnly && (
                        <button type="submit" disabled={busy || !dirty}>{busy ? '…' : T.apply}</button>
                    )}
                </div>
                {readOnly && <div className="notice">{T.readOnlyNotice}</div>}
                {error && <div className="notice err">{error}</div>}
            </div>

            <fieldset disabled={busy || readOnly} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="panel">
                    <h2 style={{ marginTop: 0 }}>{rec.title || `#${rec.itemId}`}</h2>
                    <p className="hint" style={{ marginTop: 0 }}>
                        {brandName(rec.brand)} · #{rec.itemId} · {rec.type || '—'} · v{rec.version}
                        {rec.freeAccessCount > 0 && fill(plural(rec.freeAccessCount, T.freeAccess), { n: rec.freeAccessCount })}
                    </p>
                    <p className="hint" style={{ marginBottom: 0 }}>
                        {T.content}{rec.contentReleased ? T.released : T.contentOpen}{T.contentSep}
                        {' '}{T.sales}{rec.salesReleased ? T.released : T.salesLocked}
                        {rec.sellable && <span style={{ color: 'var(--ok)' }}>{T.sellableSuffix}</span>}
                        {/* Toggled from the list — a one-click action, not form editing. */}
                    </p>
                </div>

                <div className="panel">
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 160 }}>
                            <label>{fill(T.salesPriceLabel, { sign: moneySign() })}</label>
                            <input type="number" step="0.01" min="0" value={form.salesPrice} placeholder="—"
                                onChange={(e) => set({ salesPrice: e.target.value })} />
                        </div>
                        <div className="field" style={{ maxWidth: 160 }}>
                            <label>{T.suggestionLabel}</label>
                            <div style={{ paddingTop: 8 }}>
                                {money(rec.priceSuggestion)}
                                {suggestionDiffers && <span className="hint" title={T.suggestionDiffersTitle}>{T.suggestionDiffers}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 200 }}>
                            <label>{T.feeTypeLabel}<InfoTip>{T.feeTypeTip}</InfoTip></label>
                            <select value={form.feeType} onChange={(e) => set({ feeType: e.target.value })}>
                                <option value="">{T.inherit}</option>
                                <option value="percent">{T.percent}</option>
                                <option value="fixed">{T.fixed}</option>
                            </select>
                        </div>
                        <div className="field" style={{ maxWidth: 160 }}>
                            <label>{T.feeValueLabel}</label>
                            <input type="number" step="0.01" min="0" value={form.feeValue} disabled={!form.feeType}
                                onChange={(e) => set({ feeValue: e.target.value })} />
                        </div>
                        <div className="field">
                            <label>{T.effectiveLabel}</label>
                            <div className="hint" style={{ paddingTop: 10 }}>
                                {withAuthor
                                    ? `${rec.effectiveFee.value}${rec.effectiveFee.type === 'fixed' ? ` ${moneySign()}` : ' %'} (${FEE_LEVEL[rec.effectiveFee.level]})`
                                    : T.feeNoEffect}
                            </div>
                        </div>
                    </div>

                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 320 }}>
                            <label>{T.authorLabel}</label>
                            {withAuthor ? (
                                <select value={form.authorId} onChange={(e) => set({ authorId: e.target.value })}>
                                    <option value="">{T.noAuthor}</option>
                                    {authors.map((a) => <option key={a._id} value={a._id}>{a.displayName}</option>)}
                                </select>
                            ) : (
                                <div className="hint" style={{ paddingTop: 10 }} title={T.onlyCoursesTitle}>
                                    {T.onlyCourses}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    )
}
